/**
 * Origens — Provider + hook compartilhados (fonte única de verdade).
 */
import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { Origin } from '../../../../types';
import { originsRepository } from '../../../../infrastructure/repositories/SupabaseOriginsRepository';
import { useOrganization } from '../../../../contexts/OrganizationContext';

interface OriginsContextData {
    origins: Origin[];
    loading: boolean;
    createOrigin: (origin: Omit<Origin, 'id' | 'createdAt'>) => Promise<Origin | undefined>;
    updateOrigin: (id: string, updates: Partial<Origin>) => Promise<void>;
    deleteOrigin: (id: string) => Promise<void>;
    deleteBulk: (ids: string[]) => Promise<void>;
    selectedIds: Set<string>;
    toggleSelection: (id: string) => void;
    toggleAllSelection: (filteredOrigins: Origin[]) => void;
    clearSelection: () => void;
    refreshOrigins: () => Promise<void>;
}

const OriginsContext = createContext<OriginsContextData | undefined>(undefined);

export const OriginsProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const { currentOrganization } = useOrganization();
    const [origins, setOrigins] = useState<Origin[]>([]);
    const [loading, setLoading] = useState(false);
    const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

    const fetchOrigins = useCallback(async () => {
        if (!currentOrganization?.id) return;

        setLoading(true);
        try {
            const data = await originsRepository.findAll(currentOrganization.id);
            setOrigins(data);
        } catch (error) {
            console.error('[useOrigins] Erro ao buscar origens:', error);
            setOrigins([]);
        } finally {
            setLoading(false);
        }
    }, [currentOrganization?.id]);

    useEffect(() => {
        if (currentOrganization?.id) {
            fetchOrigins();
        }
    }, [currentOrganization?.id, fetchOrigins]);

    const createOrigin = useCallback(async (origin: Omit<Origin, 'id' | 'createdAt'>): Promise<Origin | undefined> => {
        if (!currentOrganization?.id) return;

        const newOrigin = await originsRepository.create(origin, currentOrganization.id);
        setOrigins(prev => [...prev, newOrigin]);
        return newOrigin;
    }, [currentOrganization?.id]);

    const updateOrigin = useCallback(async (id: string, updates: Partial<Origin>) => {
        if (!currentOrganization?.id) return;

        const updatedOrigin = await originsRepository.update(id, updates, currentOrganization.id);
        setOrigins(prev => prev.map(o => (o.id === id ? updatedOrigin : o)));
    }, [currentOrganization?.id]);

    const deleteOrigin = useCallback(async (id: string) => {
        if (!currentOrganization?.id) return;

        setOrigins(prev => prev.filter(o => o.id !== id));
        setSelectedIds(prev => {
            const next = new Set(prev);
            next.delete(id);
            return next;
        });

        try {
            await originsRepository.delete(id, currentOrganization.id);
        } catch (error) {
            console.error('[useOrigins] Erro ao deletar origem:', error);
            await fetchOrigins();
            throw error;
        }
    }, [currentOrganization?.id, fetchOrigins]);

    const toggleSelection = useCallback((id: string) => {
        setSelectedIds(prev => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    }, []);

    const toggleAllSelection = useCallback((filteredOrigins: Origin[]) => {
        setSelectedIds(prev => {
            if (prev.size === filteredOrigins.length && filteredOrigins.length > 0) {
                return new Set();
            }
            return new Set(filteredOrigins.map(o => o.id));
        });
    }, []);

    const deleteBulk = useCallback(async (ids: string[]) => {
        if (!currentOrganization?.id || ids.length === 0) return;

        setOrigins(prev => prev.filter(o => !ids.includes(o.id)));
        setSelectedIds(new Set());

        try {
            await Promise.all(ids.map(id => originsRepository.delete(id, currentOrganization.id!)));
        } catch (error) {
            console.error('[useOrigins] Erro ao deletar origens em massa:', error);
            await fetchOrigins();
            throw error;
        }
    }, [currentOrganization?.id, fetchOrigins]);

    const clearSelection = useCallback(() => setSelectedIds(new Set()), []);

    const value: OriginsContextData = {
        origins,
        loading,
        createOrigin,
        updateOrigin,
        deleteOrigin,
        deleteBulk,
        selectedIds,
        toggleSelection,
        toggleAllSelection,
        clearSelection,
        refreshOrigins: fetchOrigins,
    };

    return React.createElement(OriginsContext.Provider, { value }, children);
};

export const useOrigins = (): OriginsContextData => {
    const context = useContext(OriginsContext);
    if (!context) {
        throw new Error('useOrigins must be used within OriginsProvider');
    }
    return context;
};
